import { fetchFromServer, syncDependency, type DependencySyncConfig } from "./dependency-sync";
import { categories } from "@/services/cache";
import { categoriesSyncKey } from "@/types/products/product-keys";
import type { ApiFetchResponse } from "@tmi/types/api/fetch";
import type { Categories } from "@tmi/types/api/content-models";
import type { Category } from "@tmi/types/entities";

const PAGE_SIZE = 500;

export const config: DependencySyncConfig<Categories, Category, Category> = {
    name: 'category',
    store: () => categories,
    syncKey: categoriesSyncKey,
    throttle: { hours: 4 },
    pageSize: PAGE_SIZE,
    // R26 trap 2: `skip` is a PAGE INDEX on the content catalogs, not a row offset — the producer
    // asserts it (UngatedCatalogPagingTests). `page * pageSize` returns one record in every
    // pageSize and looks perfectly healthy from here.
    buildRequest: ( page: number, pageSize: number ) => ( {
        endpoint: `content/category-catalog?skip=${ page }&limit=${ pageSize }`,
        verbOverride: 'GET'
    } ),
    // R26 trap 1: the content twins carry NO `totalCount`. Copying the sentient tickers
    // `isComplete` — `fetchedSoFar >= ( totalCount ?? MAX_SAFE_INTEGER )` — would never once be
    // true here, so every sweep would be permanently partial: never pruning, and re-running on
    // the 15-second partial-retry threshold forever. Completion is "the last page was short",
    // which is also the paging loop's own exit condition.
    isComplete: ( response: ApiFetchResponse<Categories> ) => ( response?.data?.categoryCatalog?.categories?.length ?? 0 ) < PAGE_SIZE,
    // The content twins send no ETag, and a 200 carrying `[]` from Contentful is not trustworthy
    // evidence that the catalog is empty — so no ETag alarm, and no whole-store prune.
    expectsEtag: false,
    extractPayload: ( response: ApiFetchResponse<Categories> ) => response?.data?.categoryCatalog?.categories,
    mapItem: ( c: Category ): Category => ( {
        id: c.id,
        name: c.name,
        slug: c.slug
    } )
};

export async function refreshCategoriesFromServer(): Promise<Category[]> {
    // Callers of this wrapper only want the records; partial-fetch handling lives in syncDependency.
    return ( await fetchFromServer( config ) ).items;
}

export async function loadCategoryMap( categoriesMap: Map<string, Category> ): Promise<boolean> {
    return syncDependency( config, categoriesMap );
}
